import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getRevolvingCreditLine,
  getRevolvingCreditLineList,
  getRevolvingCreditLinePendingCharges,
  submitDrawRequest,
  submitPaymentRequest,
  exportRevolvingCreditLineMovements,
  getRclRequests,
  submitRclRequest,
} from '../services/servicing';
import {
  RevolvingCreditLineClient,
  RevolvingCreditLineClientDetail,
  RevolvingCreditLinePendingCharges,
} from '../types/servicing';
import { PaginatedResponse } from '../services/types';

export const REVOLVING_CREDIT_LINE_LIST_QUERY_KEY = 'revolving-credit-line-list';
export const REVOLVING_CREDIT_LINE_DETAIL_QUERY_KEY = 'revolving-credit-line-detail';
export const REVOLVING_CREDIT_LINE_PENDING_CHARGES_QUERY_KEY = 'revolving-credit-line-pending-charges';
export const RCL_REQUESTS_QUERY_KEY = 'rcl-requests';

export const useRevolvingCreditLineList = (companyId: number | undefined) => {
  return useQuery<PaginatedResponse<RevolvingCreditLineClient>>({
    queryKey: [REVOLVING_CREDIT_LINE_LIST_QUERY_KEY, companyId],
    queryFn: () => getRevolvingCreditLineList(companyId!),
    enabled: !!companyId,
  });
};

export const useRevolvingCreditLineDetail = (
  companyId: number | undefined,
  creditLineId: string | undefined
) => {
  return useQuery<RevolvingCreditLineClientDetail>({
    queryKey: [REVOLVING_CREDIT_LINE_DETAIL_QUERY_KEY, companyId, creditLineId],
    queryFn: () => getRevolvingCreditLine(companyId!, creditLineId!),
    enabled: !!companyId && !!creditLineId,
  });
};

export const useRevolvingCreditLinePendingCharges = (
  companyId: number | undefined,
  creditLineId: string | undefined
) => {
  return useQuery<RevolvingCreditLinePendingCharges>({
    queryKey: [REVOLVING_CREDIT_LINE_PENDING_CHARGES_QUERY_KEY, companyId, creditLineId],
    queryFn: () => getRevolvingCreditLinePendingCharges(companyId!, creditLineId!),
    enabled: !!companyId && !!creditLineId,
  });
};

export const useSubmitDrawRequest = (companyId: number | undefined, creditLineId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { amount: number; currency: string; notes?: string }) =>
      submitDrawRequest(companyId!, creditLineId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [REVOLVING_CREDIT_LINE_LIST_QUERY_KEY, companyId],
      });
      queryClient.invalidateQueries({
        queryKey: [REVOLVING_CREDIT_LINE_DETAIL_QUERY_KEY, companyId, creditLineId],
      });
    },
  });
};

export const useSubmitPaymentRequest = (companyId: number | undefined, creditLineId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { amount: number; currency: string; description?: string }) =>
      submitPaymentRequest(companyId!, creditLineId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [REVOLVING_CREDIT_LINE_LIST_QUERY_KEY, companyId],
      });
      queryClient.invalidateQueries({
        queryKey: [REVOLVING_CREDIT_LINE_DETAIL_QUERY_KEY, companyId, creditLineId],
      });
      queryClient.invalidateQueries({
        queryKey: [REVOLVING_CREDIT_LINE_PENDING_CHARGES_QUERY_KEY, companyId, creditLineId],
      });
    },
  });
};

export const useExportRevolvingCreditLineMovements = () => {
  return useMutation({
    mutationFn: ({ companyId, creditLineId }: { companyId: number; creditLineId: string }) =>
      exportRevolvingCreditLineMovements(companyId, creditLineId),
    onSuccess: (blob, variables) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `credit_line_${variables.creditLineId}_movements.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};

export const useRclRequests = (companyId: number | undefined) => {
  return useQuery<any[]>({
    queryKey: [RCL_REQUESTS_QUERY_KEY, companyId],
    queryFn: () => getRclRequests(companyId!),
    enabled: !!companyId,
  });
};

export const useSubmitRclRequest = (companyId: number | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestedAmount: number) => submitRclRequest(companyId!, requestedAmount),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [RCL_REQUESTS_QUERY_KEY, companyId],
      });
    },
  });
};
